import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  AppError,
  Button,
  EmptyState,
  Loader,
  Modal,
  Search,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tag,
  toast,
} from "@bfc-software/pallet";
import { logger } from "@bfc-software/app-toolbox";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { useDeliverApi } from "@bfc-software/bfc-web-ui/lib/deliver-api-client";

import { useManageTenant } from "../../-data/use-manage-tenant";
import { QuestionImportModal } from "../-components/question-import-modal";
import {
  QuestionModal,
  type Question,
  type QuestionInput,
} from "../-components/question-modal";

export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/deliver/manage/$tenant/questions",
)({
  component: RouteComponent,
});

/**
 * The service answers with `{ error, message }` on 400/401/403/404, and plain
 * text on a 500; openapi-react-query throws whichever body it got.
 */
function errorMessage(error: unknown, fallback: string): string {
  if (typeof error === "string" && error) return error;
  if (error && typeof error === "object") {
    const body = error as { message?: unknown };
    if (typeof body.message === "string" && body.message) return body.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/**
 * Manage Warehouse > Questions: the HACCP questions Deliver asks a driver, for
 * one warehouse (a `haccp_question` row each). Add, edit and delete, over the
 * dashboard API's `/app/deliver/api/questions`, which scopes to the caller's
 * own org.
 *
 * Import copies Dakota's HACCP questions in (the config service's
 * `/orgs/deliver/questions/import*`).
 */
function RouteComponent() {
  const params = Route.useParams();
  const { tenant } = useManageTenant();
  const { t } = useTranslation({
    lang: params.lang,
    namespace: TRANSLATION_NAMESPACE.deliver,
  });

  const client = useDeliverApi();
  const queryClient = useQueryClient();
  const tenantId = tenant?.tenantId;

  const listQuery = client.useQuery(
    "get",
    "/app/deliver/api/questions",
    { params: { query: { tenantId: tenantId ?? 0 } } },
    { enabled: tenantId !== undefined },
  );
  const createMutation = client.useMutation(
    "post",
    "/app/deliver/api/questions",
  );
  const updateMutation = client.useMutation(
    "put",
    "/app/deliver/api/questions/{questionId}",
  );
  const deleteMutation = client.useMutation(
    "delete",
    "/app/deliver/api/questions/{questionId}",
  );

  const [q, setQ] = useState("");
  // `undefined` is closed, `null` is the add form, a row is the edit form.
  const [formTarget, setFormTarget] = useState<Question | null | undefined>(
    undefined,
  );
  const [deleteTarget, setDeleteTarget] = useState<Question | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const refresh = () =>
    queryClient.invalidateQueries({
      queryKey: ["get", "/app/deliver/api/questions"],
    });

  const needle = q.trim().toLowerCase();
  const visible = [...(listQuery.data?.rows ?? [])]
    .filter(
      (row) =>
        !needle ||
        row.questionCode.toLowerCase().includes(needle) ||
        row.question.toLowerCase().includes(needle),
    )
    .sort((a, b) => a.questionSeq - b.questionSeq || a.id - b.id);

  const handleSubmit = async (input: QuestionInput) => {
    const editing = formTarget ?? null;
    try {
      if (editing) {
        await updateMutation.mutateAsync({
          params: { path: { questionId: editing.id } },
          body: input,
        });
        toast.success(
          t("manage.questions.toast.updated", { code: input.questionCode }),
        );
      } else {
        await createMutation.mutateAsync({
          params: { query: { tenantId: tenantId! } },
          body: input,
        });
        toast.success(
          t("manage.questions.toast.created", { code: input.questionCode }),
        );
      }
      logger.info("Saved question", { code: input.questionCode });
      await refresh();
    } catch (error) {
      logger.warn("Failed to save question", { error });
      toast.error(errorMessage(error, t("manage.questions.form.failed")));
      throw error;
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const { id, questionCode } = deleteTarget;
    try {
      await deleteMutation.mutateAsync({
        params: { path: { questionId: id } },
      });
      logger.info("Deleted question", { id });
      toast.success(
        t("manage.questions.toast.deleted", { code: questionCode }),
      );
      setDeleteTarget(null);
      await refresh();
    } catch (error) {
      logger.warn("Failed to delete question", { error, id });
      toast.error(errorMessage(error, t("manage.questions.delete.failed")));
    }
  };

  return (
    <div className="bg-surface h-full w-full">
      <div className="page-shell">
        <header className="page-header">
          <div className="page-header-left">
            <h1 className="page-title">{t("manage.questions.heading")}</h1>
            <p className="page-subtitle">
              {tenant?.name} — {tenant?.warehouseCode}
            </p>
          </div>
          <div className="page-header-right">
            <div className="page-header-searches">
              <Search
                aria-label={t("manage.questions.searchAriaLabel")}
                placeholder={t("manage.questions.searchPlaceholder")}
                value={q}
                onChange={(value) => setQ(String(value))}
                onClear={() => setQ("")}
              />
            </div>
            <div className="page-header-actions">
              <Button
                kind="text-icon-left"
                variant="secondary"
                icon="Download"
                text={t("manage.questions.import.button")}
                isDisabled={tenant === undefined}
                onPress={() => setImportOpen(true)}
                data-test-role="import-questions"
              />
              <Button
                kind="text-icon-left"
                variant="avatar-surface"
                icon="Plus"
                text={t("manage.questions.addButton")}
                isDisabled={tenantId === undefined}
                onPress={() => setFormTarget(null)}
                data-test-role="add-question"
              />
            </div>
          </div>
        </header>

        <div className="table-frame">
          {listQuery.isLoading ? (
            <Loader type="page" />
          ) : listQuery.isError ? (
            <AppError
              description={t("manage.questions.failed")}
              resolution={t("manage.failureSupport")}
            />
          ) : (
            <Table
              aria-label={t("manage.questions.table.ariaLabel")}
              isResizable={false}
            >
              <TableHead>
                <TableHeaderCell isRowHeader={true}>
                  {t("manage.questions.table.seq")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("manage.questions.table.code")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("manage.questions.table.question")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("manage.questions.table.level")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("manage.questions.table.type")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("manage.questions.table.freq")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("manage.questions.table.answer")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("manage.questions.table.status")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("manage.questions.table.actions")}
                </TableHeaderCell>
              </TableHead>
              <TableBody>
                {visible.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <EmptyState heading={t("manage.questions.empty")} />
                    </TableCell>
                  </TableRow>
                ) : (
                  visible.map((row) => (
                    <TableRow key={row.id} data-test-role="question-row">
                      <TableCell>{row.questionSeq}</TableCell>
                      <TableCell>
                        <span className="font-mono font-strong text-on-surface">
                          {row.questionCode}
                        </span>
                      </TableCell>
                      <TableCell>{row.question}</TableCell>
                      <TableCell>{row.questionLevel}</TableCell>
                      <TableCell>{row.questionType}</TableCell>
                      <TableCell>{row.frequency}</TableCell>
                      <TableCell>{row.answerType}</TableCell>
                      <TableCell>
                        <Tag
                          label={
                            row.activeFlag
                              ? t("manage.questions.status.active")
                              : t("manage.questions.status.inactive")
                          }
                          display={{
                            tone: row.activeFlag ? "success" : "neutral",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-8">
                          <Button
                            kind="icon-only"
                            variant="tertiary"
                            icon="Edit2"
                            label={t("manage.questions.rowActions.edit")}
                            onPress={() => setFormTarget(row)}
                            data-test-role="question-edit"
                          />
                          <Button
                            kind="text-only"
                            variant="danger-container"
                            scale="compact"
                            text={t("manage.questions.rowActions.delete")}
                            onPress={() => setDeleteTarget(row)}
                            data-test-role="question-delete"
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      <QuestionImportModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={refresh}
        orgId={tenant?.orgId ?? ""}
        warehouseId={tenant?.warehouseCode ?? ""}
        t={t}
      />

      <QuestionModal
        isOpen={formTarget !== undefined}
        question={formTarget ?? null}
        onOpenChange={(open) => {
          if (!open) setFormTarget(undefined);
        }}
        onSubmit={handleSubmit}
        isPending={createMutation.isPending || updateMutation.isPending}
        t={t}
      />

      <Modal
        control="external"
        data-test-role="delete-question-modal"
        isDismissable={!deleteMutation.isPending}
        isOpen={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        width="standard"
        header={
          <h2 className="mb-4 text-size-18 text-on-surface">
            {t("manage.questions.delete.title")}
          </h2>
        }
      >
        <p className="mb-16 text-size-13 text-on-surface-variant">
          {deleteTarget
            ? t("manage.questions.delete.body", {
                code: deleteTarget.questionCode,
              })
            : ""}
        </p>
        <div className="mt-4 flex justify-end gap-8">
          <Button
            kind="text-only"
            variant="danger-container"
            text={
              deleteMutation.isPending
                ? t("manage.questions.delete.deleting")
                : t("manage.questions.delete.confirm")
            }
            isDisabled={deleteMutation.isPending}
            onPress={handleDelete}
            data-test-role="confirm-delete-question"
          />
          <Button
            kind="text-only"
            variant="tertiary"
            text={t("manage.questions.form.cancel")}
            onPress={() => setDeleteTarget(null)}
            isDisabled={deleteMutation.isPending}
          />
        </div>
      </Modal>
    </div>
  );
}
